/**
 * Wistia PUBLIC embed transcript — no secret required.
 *
 * Reads the public embed JSON (no token) and derives the transcript cues plus
 * the VideoObject metadata a page needs. Safe to call during a static build.
 * The secret Data-API path lives in `./wistia.ts`; this module never touches it.
 */
import {
  parseCaptions,
  plaintextToCues,
  type TranscriptCue,
} from "./transcript";

const WISTIA_EMBED_JSON = "https://fast.wistia.net/embed/medias";
const WISTIA_HLS = "https://fast.wistia.com/embed/medias";

/** Wistia hashed IDs are alphanumeric; strip anything else to avoid path/SSRF injection. */
function sanitizeWistiaId(hashedId: string): string {
  return hashedId.replace(/[^a-z0-9]/gi, "");
}

function isWistiaHost(hostname: string): boolean {
  return (
    hostname === "wistia.com" ||
    hostname === "wistia.net" ||
    hostname.endsWith(".wistia.com") ||
    hostname.endsWith(".wistia.net")
  );
}

/** Public HLS manifest URL for the branded custom player. No secret required. */
export function getWistiaHlsUrl(hashedId: string): string {
  return `${WISTIA_HLS}/${sanitizeWistiaId(hashedId)}.m3u8`;
}

const WISTIA_MANIFEST_TIMEOUT_MS = 4000;

/**
 * Whether a Wistia id's HLS manifest lists at least one real variant. Some
 * older uploads only have an MP4 rendition and return a structurally valid
 * but empty manifest (header lines only) — that's the only reliable signal,
 * since the media JSON's asset list doesn't distinguish them. Fails closed
 * (false) on a non-2xx response, fetch error, or timeout.
 */
export async function hasPlayableHlsManifest(
  hashedId: string,
): Promise<boolean> {
  try {
    const res = await fetch(getWistiaHlsUrl(hashedId), {
      signal: AbortSignal.timeout(WISTIA_MANIFEST_TIMEOUT_MS),
    });
    if (!res.ok) return false;
    const text = await res.text();
    return text
      .split("\n")
      .some((line) => line.trim().length > 0 && !line.trim().startsWith("#"));
  } catch {
    return false;
  }
}

/**
 * Extract the Wistia hashed id from an embed/HLS/iframe URL — `/medias/<id>`
 * (HLS manifests, `intercom.wistia.com`, `product-education.wistia.com`) or
 * `/embed/iframe/<id>` (the canonical form `toEmbedSrc` normalises to).
 * Returns "" when the host isn't Wistia or neither segment is present —
 * other allowed embed hosts could coincidentally share that path shape.
 */
export function wistiaIdFromUrl(url: string): string {
  let hostname: string;
  try {
    hostname = new URL(url).hostname;
  } catch {
    return "";
  }
  if (!isWistiaHost(hostname)) return "";
  return sanitizeWistiaId(
    url.match(/\/(?:medias|iframe)\/([a-z0-9]+)/i)?.[1] ?? "",
  );
}

export type WistiaEmbedData = {
  cues: TranscriptCue[];
  name: string;
  durationSeconds: number;
  thumbnailUrl: string;
  contentUrl: string;
};

type EmbedAsset = { type?: string; url?: string };
type EmbedCaption = { language?: string; text?: string };
type EmbedPayload = {
  media?: {
    name?: string;
    duration?: number;
    assets?: EmbedAsset[];
    captions?: EmbedCaption[];
  };
};

function pickThumbnail(assets: EmbedAsset[] | undefined): string {
  const still = assets?.find((a) => a.type === "still_image" && a.url);
  const url = still?.url ?? assets?.find((a) => a.url)?.url ?? "";
  // Wistia still-image delivery URLs end in `.bin`; the `.jpg` variant serves a real image.
  return url.replace(/\.bin(?=$|[?#])/, ".jpg");
}

function pickCaptionText(captions: EmbedCaption[] | undefined): string {
  if (!captions?.length) return "";
  const eng = captions.find((c) => c.language === "eng" && c.text?.trim());
  return (eng ?? captions.find((c) => c.text?.trim()))?.text ?? "";
}

export async function fetchWistiaEmbedTranscript(
  hashedId: string,
): Promise<WistiaEmbedData | null> {
  const id = sanitizeWistiaId(hashedId);
  try {
    const res = await fetch(`${WISTIA_EMBED_JSON}/${id}.json`, {
      cache: "force-cache",
    });
    if (!res.ok) throw new Error(`embed request failed: ${res.status}`);

    const raw: unknown = await res.json();
    if (typeof raw !== "object" || raw === null) {
      throw new Error("embed payload is not an object");
    }
    const media = (raw as EmbedPayload).media;
    if (!media?.name) throw new Error("embed payload missing media.name");

    const durationSeconds = Math.round(media.duration ?? 0);
    const captionText = pickCaptionText(media.captions);
    let cues = parseCaptions(captionText);
    if (cues.length === 0 && captionText.trim()) {
      cues = plaintextToCues(captionText, durationSeconds);
    }

    return {
      cues,
      name: media.name,
      durationSeconds,
      thumbnailUrl: pickThumbnail(media.assets),
      contentUrl: getWistiaHlsUrl(id),
    };
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `[wistia-embed] transcript fetch failed for "${id}":`,
        error,
      );
    }
    return null;
  }
}
